<template>
  <div class="reporting-grid">
    <ag-grid-vue
      style="width: 100%; height: 100%;"
      class="ag-theme-custom"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :loading="loading"
      :default-col-def="defaultColDef"
      :auto-group-column-def="autoGroupColumnDef"
      :group-default-expanded="3"
      :row-data="loading ? null : processedReportingAggregations"
      @grid-ready="onGridReady"
      @first-data-rendered="onFirstDataRendered"
    />
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */

import { AgGridVue } from 'ag-grid-vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { get, isNil } from 'lodash';
import numberFormats from '@enums/number-formats';
import agGridCellRendererComponentWrapper from '../../../components/ag-grid-cell-renderers/ag-grid-cell-renderer-component-wrapper.vue';
import {
  competitorIndexWeightingTypes,
  displayInYearImpact,
  showUnsimulatedIndex,
  extraDecimalPlacesForMarginDelta,
  useAbsoluteCompetitorIndex,
  useForecastVolume,
  useMathematicalDelta,
} from '@enums/feature-flags';
import reportingCustomHeader from './reporting-custom-header.vue';
import reportingHierarchyCellRenderer from './reporting-hierarchy-cell-renderer.vue';
import reportingControlsCellRenderer from './reporting-controls-cell-renderer.vue';
import featureFlagsMixin from '../../../mixins/featureFlags';
import { transformCompetitorsWithMathDelta } from '../../../utils/competitor-delta-utils';

export default {
  name: 'ReportingAgGrid',

  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    reportingCustomHeader,
    // eslint-disable-next-line vue/no-unused-components
    reportingHierarchyCellRenderer,
    // eslint-disable-next-line vue/no-unused-components
    reportingControlsCellRenderer,
  },

  mixins: [featureFlagsMixin],

  data() {
    return {
      gridApi: null,
      gridOptions: {
        accentedSort: true,
        enableFillHandle: true,
        enableRangeSelection: true,
        fillHandleDirection: 'y',
        headerHeight: 80,
        rowHeight: 40,
        singleClickEdit: true,
        stopEditingWhenCellsLoseFocus: true,
        suppressRowDrag: true,
        suppressContextMenu: true,
        tooltipShowDelay: 200,
        treeData: true,
        getDataPath: data => data.hierarchyPath,
        getRowId: params => params.data._id || params.data.levelEntryKey,
        components: {
          agGridCellRendererComponentWrapper,
        },
      },
      defaultColDef: {
        autoHeaderHeight: true,
        editable: false,
        resizable: true,
        sortable: false, // Disable sorting on all columns by default
        width: 70,
        wrapHeaderText: true,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
      },
      autoGroupColumnDef: {
        headerName: '',
        pinned: 'left',
        minWidth: 300,
        maxWidth: 500,
        cellRendererParams: {
          suppressCount: true,
          innerRendererSelector: params => ({
            component: 'reporting-hierarchy-cell-renderer',
            params,
          }),
        },
      },
    };
  },

  computed: {
    ...mapState('gridView', ['selectedWeighting', 'selectedCompetitors', 'showRegularImpact']),
    ...mapState('reporting', [
      'reportingAggregations',
      'expandedRow',
      'splitData',
      'splitLoading',
      'selectedAttributes',
      'loading',
    ]),
    ...mapGetters('hierarchy', ['getHierarchy']),

    /**
     * Processes reporting aggregations to dynamically insert split child rows
     * when a hierarchy row is expanded (expand rows inline strategy).
     *
     * Split rows can be either store groups or attribute values, depending on the splitBy type.
     * Store group rows get hierarchyPath: [...parentPath, 'Store Group: {name}'] and isStoreGroupRow: true
     * Attribute rows get hierarchyPath: [...parentPath, '{attributeKey}: {value}'] and isAttributeRow: true
     * Only one row can be expanded at a time.
     *
     * @returns {Array|null} Row data with split children inserted, or null if loading
     */
    processedReportingAggregations() {
      if (!this.reportingAggregations || this.loading) return null;

      const processedData = [...this.reportingAggregations];

      // Apply mathematical delta transformation to all main hierarchy rows
      processedData.forEach(row => {
        if (this.shouldUseMathematicalDelta) {
          // note no fallback to old delta here, different to grid-view aggregations-row.vue
          row.marginDelta = row.marginProposed - row.marginLive;

          if (row.competitorsByKey) {
            row.competitorsByKey = transformCompetitorsWithMathDelta(
              row.competitorsByKey,
              this.shouldUseMathematicalDelta,
              this.shouldShowUnsimulatedIndex
            );
          }
        }
      });

      // Seed stable row ids for deterministic sibling placement
      processedData.forEach((row, idx) => {
        if (!row._id) row._id = row.levelEntryKey || `row-${idx}`;
      });

      // If there's an expanded row, add the split children
      // Split data is already sorted by the backend
      if (this.expandedRow && this.splitData[this.expandedRow.levelEntryKey]) {
        const parentIndex = processedData.findIndex(
          row => row.levelEntryKey === this.expandedRow.levelEntryKey
        );

        if (parentIndex !== -1) {
          const parentRow = processedData[parentIndex];
          const expandedData = this.splitData[this.expandedRow.levelEntryKey];
          const isAttributeSplit = this.expandedRow.groupBy !== 'storeGroups';

          const childRows = expandedData.map((row, i) => {
            let transformedRow;
            if (isAttributeSplit) {
              // Attribute row - use display name from selectedAttributes if available
              const selectedAttr = (this.selectedAttributes || []).find(
                attr => attr.key === this.expandedRow.selectedOption
              );
              const attributeLabel = get(selectedAttr, 'displayDescription', row.attributeKey);

              transformedRow = {
                _id: `${parentRow.levelEntryKey}::attr::${row.attributeValue}`,
                ...row,
                hierarchyPath: [
                  ...parentRow.hierarchyPath,
                  `${attributeLabel}: ${row.attributeValue}`,
                ],
                level: parentRow.level + 1,
                isAttributeRow: true,
              };
            } else {
              // Store group row
              transformedRow = {
                _id: `${parentRow.levelEntryKey}::sg::${row.toolStoreGroupKey || row.name || i}`,
                ...row,
                hierarchyPath: [
                  ...parentRow.hierarchyPath,
                  `Store Group: ${row.name || row.toolStoreGroupKey}`,
                ],
                level: parentRow.level + 1,
                isStoreGroupRow: true,
              };
            }

            // Apply mathematical delta transformation to split child rows
            if (this.shouldUseMathematicalDelta && transformedRow.competitorsByKey) {
              transformedRow.competitorsByKey = transformCompetitorsWithMathDelta(
                transformedRow.competitorsByKey,
                this.shouldUseMathematicalDelta,
                this.shouldShowUnsimulatedIndex
              );
            }

            return transformedRow;
          });

          // Insert child rows immediately after the parent row
          processedData.splice(parentIndex + 1, 0, ...childRows);
        }
      }

      return processedData;
    },

    columnDefs() {
      return [
        {
          // Virtual column for dropdown and details controls - narrow but functional
          headerName: '',
          field: '_controls',
          cellRenderer: 'reportingControlsCellRenderer',
          pinned: 'left',
          width: 160,
          minWidth: 160,
          maxWidth: 160,
          suppressSizeToFit: true,
          suppressAutoSize: true,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
          suppressColumnsToolPanel: true,
          headerClass: ['controls-column-header'],
          cellClass: ['controls-column-cell'],
        },
        {
          headerGroupComponent: 'reportingCustomHeader',
          headerGroupComponentParams: {
            headerLine1: this.$t('gridView.salesMetrics'),
            toggleValue: this.showRegularImpact,
            trueValue: true,
            falseValue: false,
            toggleLabel: this.$t(`gridView.regularImpactToggle.label`),
            onToggle: showRegularImpact => {
              this.setShowRegularImpact({ showRegularImpact });
            },
          },
          headerClass: ['border-right', 'border-left', 'custom-group-header'],
          groupId: 'salesMetrics',
          marryChildren: true,
          children: [
            {
              headerName: this.$t(
                `gridView.${
                  this.isFeatureFlagEnabled(useForecastVolume) ? 'forecastSales' : 'salesMAT'
                }`
              ),
              headerClass: 'border-left',
              field: this.isFeatureFlagEnabled(useForecastVolume) ? 'forecastSales' : 'salesMAT',
              cellClass: ['numeric-cell', 'border-left'],
              valueFormatter: params =>
                !isNil(params.value)
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.integer,
                    })
                  : '-',
            },
            {
              headerName: this.$t('gridView.promoShare'),
              field: 'promoShare',
              cellClass: 'numeric-cell',
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.percent,
                  zeroAsDash: true,
                }),
            },
            {
              headerName: this.showRegularImpact
                ? this.$t('gridView.regularSalesImpact')
                : this.$t('gridView.salesImpactMAT'),
              field: this.showRegularImpact ? 'regularSalesImpact' : 'salesImpactMAT',
              cellClass: params => {
                return [this.formatNumberColourClass(params.value), 'numeric-cell', 'highlight'];
              },
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value / 1000,
                  format: numberFormats.integer,
                  zeroAsDash: true,
                }),
              tooltipValueGetter: params => {
                if (!params.data) return null;
                const label = this.showRegularImpact
                  ? this.$t('gridView.regularImpactInYear')
                  : this.$t('gridView.totalImpactInYear');
                const value = this.showRegularImpact
                  ? params.data.regularImpactInYear / 1000
                  : params.data.totalImpactInYear / 1000;
                return this.isFeatureFlagEnabled(displayInYearImpact)
                  ? `${label}: ${this.formatNumber({
                      number: value,
                      format: numberFormats.integer,
                      zeroAsDash: true,
                    })}`
                  : null;
              },
            },
            {
              headerName: this.$t('gridView.totalCostImpact'),
              headerClass: 'border-right',
              field: this.showRegularImpact ? 'regularCostImpact' : 'totalCostImpact',
              cellClass: params => {
                return [
                  this.formatNumberColourClass(-params.value),
                  'numeric-cell',
                  'highlight',
                  'border-right',
                ];
              },
              valueFormatter: params =>
                this.formatNumber({
                  number: -params.value / 1000,
                  format: numberFormats.integer,
                  zeroAsDash: true,
                }),
              tooltipValueGetter: params => {
                if (!params.data) return null;
                const value = this.showRegularImpact
                  ? -params.data.regularCostImpactInYear / 1000
                  : -params.data.costImpactInYear / 1000;
                return this.isFeatureFlagEnabled(displayInYearImpact)
                  ? `${this.$t('gridView.costImpactInYear')}: ${this.formatNumber({
                      number: value,
                      format: numberFormats.integer,
                      zeroAsDash: true,
                    })}`
                  : null;
              },
            },
          ],
        },
        {
          headerName: this.$t('gridView.margin'),
          headerClass: ['border-right', 'regular-group-header'],
          groupId: 'margin',
          marryChildren: true,
          children: [
            {
              headerName: this.$t('gridView.liveMargin'),
              field: 'marginLive',
              cellClass: 'numeric-cell',
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: this.isFeatureFlagEnabled(extraDecimalPlacesForMarginDelta)
                    ? numberFormats.percentWithExtraDecimalPlaces
                    : numberFormats.percent,
                  zeroAsDash: true,
                }),
            },
            {
              headerName: this.$t('gridView.proposedMargin'),
              field: 'marginProposed',
              cellClass: 'numeric-cell',
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: this.isFeatureFlagEnabled(extraDecimalPlacesForMarginDelta)
                    ? numberFormats.percentWithExtraDecimalPlaces
                    : numberFormats.percent,
                  zeroAsDash: true,
                }),
            },
            {
              headerName: this.$t('gridView.delta'),
              headerClass: 'border-right',
              field: 'marginDelta',
              cellClass: params => {
                return [this.formatNumberColourClass(params.value), 'numeric-cell', 'border-right'];
              },
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: this.isFeatureFlagEnabled(extraDecimalPlacesForMarginDelta)
                    ? numberFormats.percentWithExtraDecimalPlaces
                    : numberFormats.percent,
                  zeroAsDash: true,
                }),
            },
          ],
        },
        ...this.selectedCompetitors.map(
          ({ competitorKey, competitorDisplayDescription }, index) => {
            return {
              ...(index === 0
                ? {
                    headerGroupComponent: 'reportingCustomHeader',
                    headerGroupComponentParams: {
                      headerLine1: this.$t('gridView.priceDistance'),
                      headerLine2: competitorDisplayDescription,
                      displayCompetitorsSelection: true,
                      toggleValue: this.selectedWeighting,
                      trueValue: competitorIndexWeightingTypes.volume,
                      falseValue: competitorIndexWeightingTypes.sales,
                      toggleLabel: this.$t(
                        `gridView.weightings.${competitorIndexWeightingTypes.volume}`
                      ),
                      onToggle: selectedWeighting => {
                        this.setSelectedWeighting({ selectedWeighting });
                      },
                    },
                    headerClass: ['border-right', 'custom-group-header'],
                  }
                : {
                    headerName: competitorDisplayDescription,
                    headerClass: ['border-right', 'competitor-group-header'],
                  }),
              groupId: competitorKey,
              marryChildren: true,
              children: [
                {
                  headerName: this.$t('gridView.target'),
                  field: `${competitorKey}.targetDistance`,
                  cellClass: 'numeric-cell',
                  valueGetter: params => {
                    if (!params.data) return null;
                    const levelEntryKey = params.data.levelEntryKey;
                    const hierarchy = this.getHierarchy(h => h.levelEntryKey === levelEntryKey)[0];
                    const competitorTargetDistances = get(
                      hierarchy,
                      'competitorTargetDistances',
                      []
                    );
                    const compTD = competitorTargetDistances.find(
                      cd => cd.competitorKey === competitorKey
                    );
                    return get(compTD, 'targetDistance');
                  },
                  valueFormatter: params =>
                    this.formatNumber({
                      number: params.value,
                      format: numberFormats.priceFormat,
                      zeroAsDash: true,
                    }),
                },
                {
                  headerName: this.$t('gridView.matchedSales'),
                  field: `competitorsByKey.${competitorKey}.competitorMatchSales`,
                  cellClass: 'numeric-cell',
                  valueFormatter: params =>
                    this.formatNumber({
                      number: params.value,
                      format: numberFormats.percent,
                      zeroAsDash: true,
                    }),
                },
                {
                  headerName: this.$t('gridView.live'),
                  field: `competitorsByKey.${competitorKey}.competitorLiveDistance${
                    this.selectedWeightingFieldSuffix
                  }`,
                  cellClass: 'numeric-cell',
                  valueFormatter: this.competitorValueFormatter,
                },
                {
                  headerName: this.$t('gridView.proposed'),
                  field: `competitorsByKey.${competitorKey}.competitorProposedDistance${
                    this.selectedWeightingFieldSuffix
                  }`,
                  cellClass: 'numeric-cell',
                  valueFormatter: this.competitorValueFormatter,
                },
                ...(this.shouldShowUnsimulatedIndex
                  ? [
                      {
                        headerName: this.$t('gridView.unsimulated'),
                        field: `competitorsByKey.${competitorKey}.liveCompetitorProposedDistance${
                          this.selectedWeightingFieldSuffix
                        }`,
                        cellClass: 'numeric-cell',
                        valueFormatter: this.competitorValueFormatter,
                      },
                      {
                        headerName: this.$t('gridView.competitorDeltaSimulatedVsLive'),
                        headerTooltip: this.$t('gridView.tooltips.competitorDeltaSimulatedVsLive'),
                        field: `competitorsByKey.${competitorKey}.competitorDeltaSimulatedVsPrev${
                          this.selectedWeightingFieldSuffix
                        }`,
                        cellClass: params => {
                          return [this.formatNumberColourClass(params.value), 'numeric-cell'];
                        },
                        valueFormatter: this.competitorValueFormatter,
                      },
                    ]
                  : []),
                {
                  headerName: this.$t('gridView.competitorDelta'),
                  headerTooltip: this.$t('gridView.tooltips.deltaVsPrev'),
                  headerClass: 'border-right',
                  field: `competitorsByKey.${competitorKey}.competitorDeltaVsPrev${
                    this.selectedWeightingFieldSuffix
                  }`,
                  cellClass: params => {
                    return [
                      this.formatNumberColourClass(params.value),
                      'numeric-cell',
                      'border-right',
                    ];
                  },
                  valueFormatter: this.competitorValueFormatter,
                },
              ],
            };
          }
        ),
      ];
    },

    shouldUseMathematicalDelta() {
      return this.isFeatureFlagEnabled(useMathematicalDelta);
    },

    shouldShowUnsimulatedIndex() {
      return this.isFeatureFlagEnabled(showUnsimulatedIndex);
    },

    selectedWeightingFieldSuffix() {
      if (this.selectedWeighting === competitorIndexWeightingTypes.volume) {
        return 'Volume';
      }
      if (this.selectedWeighting === competitorIndexWeightingTypes.price) {
        return 'Price';
      }
      return '';
    },
  },

  watch: {
    loading() {
      if (this.gridApi) {
        this.gridApi.showLoadingOverlay();
      }
    },
  },

  methods: {
    ...mapActions('gridView', ['setSelectedWeighting', 'setShowRegularImpact']),
    ...mapActions('reporting', ['toggleRowExpansion']),

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.showLoadingOverlay();
    },

    onFirstDataRendered() {
      // Auto-size the autoGroupColumnDef (tree column) to fit content
      // This fires after data is actually rendered in cells
      if (this.gridApi) {
        // Try to find the auto-group column by AG Grid's auto-generated ID
        const allColumns = this.gridApi.getAllDisplayedColumns();
        const autoGroupColumn = allColumns.find(col => col.getColId() === 'ag-Grid-AutoColumn');

        if (autoGroupColumn) {
          this.gridApi.autoSizeColumns([autoGroupColumn], {
            skipHeader: true,
          });
        }
      }
    },

    competitorValueFormatter(params) {
      return this.formatNumber({
        number: params.value,
        format: this.isFeatureFlagEnabled(useAbsoluteCompetitorIndex)
          ? numberFormats.priceFormat
          : numberFormats.percent,
        zeroAsDash: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.reporting-grid {
  transition: height 0.5s ease-in-out;
  height: 75vh;
  width: 100%;
}

::v-deep {
  .ag-header-cell-resize::after {
    visibility: hidden;
  }

  .border-right {
    border-right: 1px solid $custom-ag-grid-header-separator-color !important;
  }

  .border-left {
    border-left: 1px solid $custom-ag-grid-header-separator-color !important;
  }

  .numeric-cell {
    justify-content: end;
    font-size: 1.3rem;
    line-height: 1.7rem;
    font-weight: 600;
  }

  .highlight {
    background-color: $grid-view-highlight;
  }

  .custom-group-header {
    align-items: start;
  }

  .regular-group-header {
    padding-top: 22px;
  }

  .competitor-group-header {
    padding-top: 72px;
    font-weight: normal;
    color: $text-color-muted;
  }

  .workpackage-link-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .workpackage-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: $custom-ag-grid-primary-color;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }

    .mdi {
      font-size: 12px;
    }
  }
}

.controls-column-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  background-color: $pricing-white;
}
</style>
